import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useI18n } from '@/lib/i18n';
import { Phone, EnvelopeSimple, MapPin, ArrowRight, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({
    inquiry_type: 'general',
    name: '', company: '', phone: '', email: '', subject: '', message: '',
  });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      setForm((f) => ({ ...f, inquiry_type: e.detail || 'general' }));
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
    };
    window.addEventListener('set-inquiry', handler);
    return () => window.removeEventListener('set-inquiry', handler);
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setStatus(null);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data?.status === 'success') {
        setStatus('success');
        toast.success(t.contact.form.success);
        setForm({ inquiry_type: 'general', name: '', company: '', phone: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
        toast.error(t.contact.form.error);
      }
    } catch {
      setStatus('error');
      toast.error(t.contact.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#F8F9FA] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow text-[#C5A059] flex items-center gap-3">
            <span className="w-10 h-px bg-[#C5A059]" />
            {t.contact.eyebrow}
          </div>
          <h2 className="heading-display mt-6 text-[#0A1128] text-3xl sm:text-4xl lg:text-5xl">{t.contact.title}</h2>
          <p className="mt-6 text-[#1E2229]/70 text-lg leading-relaxed">{t.contact.sub}</p>

          <div className="mt-10 space-y-6">
            <div className="flex items-start gap-4">
              <Phone size={20} weight="thin" className="text-[#C5A059] mt-1" />
              <div>
                <div className="eyebrow text-[#64748B]">{t.contact.info.phone}</div>
                <a href="tel:+4915208483930" data-testid="contact-phone" className="text-[#0A1128] hover:text-[#C5A059] transition-colors text-lg">+49 152 08483930</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <EnvelopeSimple size={20} weight="thin" className="text-[#C5A059] mt-1" />
              <div>
                <div className="eyebrow text-[#64748B]">{t.contact.info.email}</div>
                <a href="mailto:info@demelo-recruiting.de" data-testid="contact-email" className="text-[#0A1128] hover:text-[#C5A059] transition-colors">info@demelo-recruiting.de</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin size={20} weight="thin" className="text-[#C5A059] mt-1" />
              <div>
                <div className="eyebrow text-[#64748B]">{t.contact.info.location}</div>
                <div className="text-[#0A1128]">{t.contact.info.locationVal}</div>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <iframe
              title="Map – Freiburg im Breisgau"
              data-testid="contact-map"
              src="https://www.google.com/maps?q=Freiburg+im+Breisgau,+Germany&output=embed"
              className="w-full h-72 border border-[#E2E8F0]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <form
          ref={formRef}
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-white border border-[#E2E8F0] p-8 md:p-12"
        >
          <h3 className="font-display text-2xl text-[#0A1128]">{t.contact.formTitle}</h3>
          <div className="mt-3 h-px w-12 bg-[#C5A059]" />

          <div className="mt-8">
            <div className="eyebrow text-[#64748B] mb-3">{t.contact.form.type}</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {Object.entries(t.contact.form.types).map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  data-testid={`inquiry-${key}`}
                  onClick={() => update('inquiry_type', key)}
                  className={`px-4 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase border transition-all ${
                    form.inquiry_type === key
                      ? 'bg-[#0A1128] text-white border-[#0A1128]'
                      : 'bg-white text-[#0A1128] border-[#E2E8F0] hover:border-[#C5A059] hover:text-[#C5A059]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-2 gap-x-8 gap-y-6">
            <Field testid="field-name" label={t.contact.form.name} required>
              <input required value={form.name} onChange={(e) => update('name', e.target.value)} className="input-line" />
            </Field>
            <Field testid="field-company" label={t.contact.form.company}>
              <input value={form.company} onChange={(e) => update('company', e.target.value)} className="input-line" />
            </Field>
            <Field testid="field-email" label={t.contact.form.email} required>
              <input required type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className="input-line" />
            </Field>
            <Field testid="field-phone" label={t.contact.form.phone}>
              <input type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} className="input-line" />
            </Field>
            <div className="md:col-span-2">
              <Field testid="field-subject" label={t.contact.form.subject}>
                <input value={form.subject} onChange={(e) => update('subject', e.target.value)} className="input-line" />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field testid="field-message" label={t.contact.form.message} required>
                <textarea required rows={5} value={form.message} onChange={(e) => update('message', e.target.value)} className="input-line resize-none" />
              </Field>
            </div>
          </div>

          <div className="mt-10 flex items-center gap-6 flex-wrap">
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={sending}
              className="group inline-flex items-center gap-3 bg-[#0A1128] hover:bg-[#C5A059] hover:text-[#0A1128] text-white px-8 py-4 text-[12px] font-semibold tracking-[0.22em] uppercase transition-all disabled:opacity-60"
            >
              {sending ? t.contact.form.sending : t.contact.form.submit}
              <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </button>
            {status === 'success' && (
              <div data-testid="contact-success" className="flex items-center gap-2 text-emerald-700 text-sm">
                <CheckCircle size={18} weight="fill" />{t.contact.form.success}
              </div>
            )}
            {status === 'error' && (
              <div data-testid="contact-error" className="flex items-center gap-2 text-red-700 text-sm">
                <WarningCircle size={18} weight="fill" />{t.contact.form.error}
              </div>
            )}
          </div>
        </form>
      </div>

      <style>{`
        .input-line {
          width: 100%;
          background: transparent;
          border: 0;
          border-bottom: 1px solid #E2E8F0;
          padding: 12px 0;
          font-size: 15px;
          color: #0A1128;
          outline: none;
          transition: border-color .3s ease;
        }
        .input-line:focus { border-color: #C5A059; }
      `}</style>
    </section>
  );
}

function Field({ label, children, required, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="eyebrow text-[#64748B]">{label}{required && <span className="text-[#C5A059]"> *</span>}</span>
      {children}
    </label>
  );
}
